import { PERSON_TYPES } from "@releve/shared";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useTranslation } from "@/hooks/useTranslation";

interface PersonTypeToggleGroupProps {
  value: string[];
  onChange: (value: string[]) => void;
  /** Accessible name of the group (the visible label it sits under). */
  ariaLabel: string;
  disabled?: boolean;
}

/**
 * The person-type multi-select: a keyboard-accessible `ToggleGroup` (arrow
 * keys move, Space toggles) over the shared `PERSON_TYPES` vocabulary, with
 * labels from `common:people.types.*`. A legacy token the vocabulary does not
 * know is kept in `value` untouched (it is simply not offered as a toggle).
 */
export function PersonTypeToggleGroup({ value, onChange, ariaLabel, disabled }: PersonTypeToggleGroupProps) {
  const { t } = useTranslation("people");
  const legacy = value.filter((type) => !(PERSON_TYPES as readonly string[]).includes(type));

  return (
    <ToggleGroup
      type="multiple"
      variant="outline"
      size="sm"
      aria-label={ariaLabel}
      value={value.filter((type) => (PERSON_TYPES as readonly string[]).includes(type))}
      onValueChange={(next) => onChange([...next, ...legacy])}
      disabled={disabled}
      className="flex-wrap justify-start"
    >
      {PERSON_TYPES.map((type) => (
        <ToggleGroupItem key={type} value={type}>
          {t(`common:people.types.${type}`)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
